import { useState, useCallback, useRef, useEffect, useLayoutEffect, useMemo } from 'react';

/** Máximo de cámaras simultáneas en la grilla */
export const MAX_CAMERAS = 4;

/** Duración del timeline en horas (24h del día) */
export const TIMELINE_HOURS = 24;
/** Milisegundos en la ventana del día (para zoom/pan en la UI de Grabaciones) */
export const TIMELINE_MS = TIMELINE_HOURS * 60 * 60 * 1000;

function readRecordingMinutesEnv(name: string, fallback: number, min: number, max: number): number {
  try {
    const raw = process.env[name];
    if (raw == null || String(raw).trim() === '') return fallback;
    const n = parseInt(String(raw).trim(), 10);
    if (!Number.isFinite(n)) return fallback;
    return Math.min(max, Math.max(min, n));
  } catch {
    return fallback;
  }
}

/** Minutos hacia adelante desde el ancla en el `prepare` (sin contar el pre-roll hasta el ancla). */
const _playbackForwardMin = readRecordingMinutesEnv(
  'REACT_APP_RECORDING_PLAYBACK_FORWARD_MINUTES',
  10,
  1,
  120
);
/** Minutos hacia atrás desde el ancla (pre-roll): el rango pedido empieza en ancla − pre-roll. */
const _preRollMin = readRecordingMinutesEnv('REACT_APP_RECORDING_PRE_ROLL_MINUTES', 3, 0, 120);
const _playbackMaxMin = readRecordingMinutesEnv(
  'REACT_APP_RECORDING_MAX_PLAYLIST_MINUTES',
  _preRollMin + _playbackForwardMin,
  1,
  120
);
export const RECORDING_PLAYBACK_FORWARD_MINUTES = _playbackForwardMin;
export const RECORDING_PRE_ROLL_MINUTES = _preRollMin;
/**
 * En el día calendario actual, el puntero no puede pasar de (hora local actual − este margen):
 * evita pedir segmentos que el grabador aún no ha materializado en NFS.
 * Default 2 min (antes 20); configurable con REACT_APP_RECORDING_TIMELINE_LAG_MINUTES.
 * Bajarlo permite ver grabaciones más recientes; si el grabador tarda en escribir a NFS,
 * súbelo para no pedir segmentos aún inexistentes.
 */
const _timelineLagMin = readRecordingMinutesEnv('REACT_APP_RECORDING_TIMELINE_LAG_MINUTES', 2, 0, 120);
export const RECORDING_TIMELINE_LAG_BEHIND_NOW_MS = _timelineLagMin * 60 * 1000;
/**
 * Máximo de minutos de playlist desde `startTime` hasta `endTime` (backend / motor).
 * Debe cubrir al menos pre-roll + forward (el cap del API cuenta desde el inicio del rango, no desde el ancla).
 */
export const RECORDING_MAX_PLAYLIST_MINUTES = Math.max(
  _playbackMaxMin,
  _playbackForwardMin,
  _preRollMin + _playbackForwardMin
);
/** Por defecto forward − 3: ~3 min de margen antes del fin de la ventana HLS para auto-extender (si está activo). */
const _rollRaw = readRecordingMinutesEnv('REACT_APP_RECORDING_ROLL_AFTER_PLAYED_MINUTES', 7, 1, 120);
/**
 * Tras ~este tiempo de avance en tiempo de grabación desde el ancla, se prepara la siguiente ventana
 * (solo si `RECORDING_AUTO_EXTEND_PLAYLIST_WHILE_PLAYING` es true).
 * Queda acotado a (forward − 1) min como máximo para no superar la ventana HLS.
 */
export const RECORDING_ROLL_AFTER_PLAYED_MINUTES = Math.max(
  1,
  Math.min(_rollRaw, Math.max(1, RECORDING_PLAYBACK_FORWARD_MINUTES - 1))
);
/**
 * Cuánto puede quedar de la ventana HLS (minutos) para mostrar el toast manual de “cargar siguientes”.
 * No reutiliza el umbral de auto-roll (antes ~3–5 min y molestaba a mitad de la ventana forward).
 * Default 1 min; configurable con REACT_APP_RECORDING_NEAR_END_OFFER_MINUTES.
 */
const _nearEndOfferRaw = readRecordingMinutesEnv('REACT_APP_RECORDING_NEAR_END_OFFER_MINUTES', 1, 1, 10);
export const RECORDING_NEAR_END_OFFER_REMAINING_MINUTES = Math.min(
  _nearEndOfferRaw,
  Math.max(1, RECORDING_PLAYBACK_FORWARD_MINUTES - 1)
);
/**
 * Si es true, durante PLAYING al acercarse al fin de la ventana HLS se pide otro `/playback/request`
 * para seguir reproduciendo (nuevo m3u8). Si es false, no se recarga el manifiesto en automático: basta una
 * carga con la ventana configurada (`REACT_APP_RECORDING_*`); para otro tramo el usuario mueve el timeline o re-prepara.
 */
export const RECORDING_AUTO_EXTEND_PLAYLIST_WHILE_PLAYING = false;

function isSameLocalCalendarDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/** Fin efectivo del timeline: fin del día salvo en “hoy”, donde se recorta a ahora − margen. */
export function computeRecordingTimelineEffectiveEnd(windowStart: Date, wallNow: Date): Date {
  const dayEnd = new Date(windowStart.getTime() + TIMELINE_MS);
  if (!isSameLocalCalendarDay(windowStart, wallNow)) {
    return dayEnd;
  }
  const latest = wallNow.getTime() - RECORDING_TIMELINE_LAG_BEHIND_NOW_MS;
  const capped = Math.min(dayEnd.getTime(), latest);
  return new Date(Math.max(windowStart.getTime(), capped));
}

/** Tolerancia de sincronización (segundos): seek si un reproductor se desvía más (grabaciones HLS: más holgura para no pelear con la carga de .ts). */
export const SYNC_TOLERANCE_SEC = 3.5;
/**
 * Si true, durante PLAYING un intervalo re-alinea cada cámara al reloj maestro (misma hora de grabación en todos).
 * Si false (recomendado para XTAM): misma hora al cargar y al pulsar Play; adelantar/retroceder/velocidad/stop
 * se aplican a todos, pero cada `<video>` avanza con su propio buffer sin seeks correctores continuos.
 */
export const RECORDING_ENFORCE_MULTICAM_TIMELINE_DRIFT_SYNC = false;
/** Intervalo entre comprobaciones de sync (ms); más espaciado reduce seeks en ráfaga. */
export const SYNC_INTERVAL_MS = 2000;
/** Tras un seek de sync por slot, no volver a seek en ese slot hasta pasado este tiempo (salvo desvío extremo). */
const SYNC_SEEK_COOLDOWN_MS = 5000;
/** Si la desviación supera esto, se hace seek aunque el slot esté en cooldown. */
const SYNC_FORCE_SEEK_DRIFT_SEC = 20;

/** Evita tormenta de prepare al borde: mínimo entre rolls automáticos por slot. */
const ROLL_PREPARE_COOLDOWN_MS = 55_000;
/**
 * En `play()`: si el objetivo cae fuera del m3u8 pero el ancla del slot sigue dentro, solo “snap” al ancla
 * cuando el salto es pequeño (desfase sync/timeline). Si el usuario eligió otra hora en el picker (p. ej.
 * 15:39 con buffer cargado a las 10:08), no sustituir esa búsqueda por el ancla vieja.
 */
const RECORDING_PLAY_SNAP_TO_ANCHOR_MAX_DRIFT_MS = 10 * 60 * 1000;

export type SeekBySecondsLocalResult =
  | { ok: true }
  | { ok: false; reason: 'no_ready_slots' | 'no_window_overlap' };

/**
 * Estados del reproductor estilo DVR/NVR:
 *  - PLAYING: reproduciendo.
 *  - PAUSED:  pausado; conserva `video.currentTime`. Play continúa desde ese frame.
 *  - STOPPED: detenido; cada `<video>` vuelve al ancla (la fecha solicitada). Play reanuda ahí.
 */
export type PlayerMode = 'PLAYING' | 'PAUSED' | 'STOPPED';
export type StreamStatus = 'IDLE' | 'PREPARING' | 'READY' | 'ERROR';

/**
 * Tracks whether a slot was added while the player was in STOPPED mode.
 * Used by play() to identify "pending" cameras that need to start when the
 * user presses Play, without conflating them with cameras that were added
 * while already PLAYING (which start immediately via isPlaying prop).
 */
export type SlotOrigin = 'added_while_stopped' | 'added_while_playing' | 'initial';

/** Playback asignado a un slot (HLS listo) */
export interface SlotPlayback {
  requestId: string;
  playlistUrl: string;
  startTime: Date;
  endTime: Date;
  startOffsetSeconds: number;
  /** Instante en la línea de tiempo que debe mostrarse al cargar (puntero / hora elegida). */
  timelineAnchorWallTime?: Date;
}

/** Cuánto tiempo de grabación puede quedar antes de `endTime` para disparar el siguiente prepare (auto-roll). */
function recordingRollRemainingThresholdMs(playback: SlotPlayback): number {
  const a = playback.timelineAnchorWallTime;
  const anchorMs =
    a != null && Number.isFinite(a.getTime()) ? a.getTime() : playback.startTime.getTime();
  const forwardSpanMs = Math.max(0, playback.endTime.getTime() - anchorMs);
  const derived = forwardSpanMs - RECORDING_ROLL_AFTER_PLAYED_MINUTES * 60 * 1000;
  return Math.min(5 * 60 * 1000, Math.max(45_000, derived));
}

/** Umbral del toast manual “cargar siguientes minutos”: solo al final real de la ventana cargada. */
function recordingNearEndOfferRemainingThresholdMs(): number {
  return RECORDING_NEAR_END_OFFER_REMAINING_MINUTES * 60 * 1000;
}

/** Estado de una cámara en un slot */
export interface CameraState {
  cameraId: string;
  cameraName: string;
  siteId: number;
  streamStatus: StreamStatus;
  playback?: SlotPlayback;
  error?: string;
}

/** Slot en la grilla: id + cámara (opcional) + estado de stream */
export interface PlayerSlot {
  id: number;
  camera: { id: string; name: string; site?: { id: string; name: string }; siteId?: string; cameraType?: string } | null;
  streamStatus: StreamStatus;
  playback?: SlotPlayback;
  error?: string;
  /** Identifica cómo fue añadida la cámara para gobernar si debe arrancar sola o esperar Play */
  origin?: SlotOrigin;
}

/** Opciones de seek: solo `skipIfClose` para Play inicial — evita vaciar MSE si ya estamos al tiempo (±15 s debe aplicarse siempre). */
export type SeekToOptions = { skipIfClose?: boolean };

/** Ref que cada CameraVideoPlayer expone para sincronización */
export interface PlaybackPlayerRef {
  getCurrentTime(): number;
  seekTo(seconds: number, options?: SeekToOptions): void;
  getDuration(): number;
  /** Inicia reproducción; retorna la Promise nativa de HTMLVideoElement.play() */
  play(): Promise<void>;
  /** Pausa el elemento <video> directamente, independiente del prop `isPlaying`. */
  pause?(): void;
  /** Opcional: captura de fotograma (CameraVideoPlayer en grabaciones). */
  captureFrame?(): string | null;
  /** true si el video no tiene suficiente data para avanzar (readyState < HAVE_FUTURE_DATA). */
  isBuffering?(): boolean;
}

export interface RecordingViewSessionReport {
  siteId: number;
  cameraId: number;
  durationSeconds: number;
  requestId?: string;
  siteName?: string;
  cameraName?: string;
  playbackRangeStart?: string;
  playbackRangeEnd?: string;
}

export interface BatchPlaybackRequest {
  slotId: number;
  siteId: number;
  cameraId: number;
  startTime: Date;
  endTime: Date;
  /** Instante objetivo (puntero). El backend lo usa para precachear los .ts del ancla. */
  anchorTime?: Date;
}

export interface BatchPlaybackResponse {
  slotId: number;
  requestId: string;
  playlistUrl?: string | null;
  startTime: Date;
  endTime: Date;
  startOffsetSeconds?: number | null;
  /** Si el backend falló para esta cámara, el mensaje de error */
  error?: string;
}

export interface UsePlayerStateProps {
  /** Número de slots (según layout); se respeta MAX_CAMERAS */
  slotCount: number;
  selectedDate: Date;
  selectedDateStr: string;
  token: string | null;
  requestPlaybackForCamera: (params: {
    siteId: number;
    cameraId: number;
    startTime: Date;
    endTime: Date;
    /** Instante objetivo (puntero). El backend lo usa para precachear los .ts del ancla. */
    anchorTime?: Date;
  }) => Promise<{
    requestId: string;
    playlistUrl?: string | null;
    startTime: Date;
    endTime: Date;
    startOffsetSeconds?: number | null;
  }>;
  /**
   * Solicita playback para varias cámaras en paralelo en una sola llamada HTTP.
   * Cuando está disponible, se usa en lugar de N llamadas individuales a `requestPlaybackForCamera`.
   */
  requestBatchPlaybackForCameras?: (cameras: BatchPlaybackRequest[]) => Promise<BatchPlaybackResponse[]>;
  
  /** Auditoría: tiempo viendo grabación mientras PLAYING (Play → Stop / cambio de ventana HLS / quitar cámara). */
  reportRecordingViewSession?: (payload: RecordingViewSessionReport) => void | Promise<void>;
  /**
   * Sin auto-roll (`RECORDING_AUTO_EXTEND_PLAYLIST_WHILE_PLAYING`): se invoca como mucho una vez
   * por cada `endTime` de ventana HLS al quedar ≤ `RECORDING_NEAR_END_OFFER_REMAINING_MINUTES` del fin, para ofrecer extender manualmente.
   */
  onRecordingWindowNearEnd?: (info: { windowEndMs: number }) => void;
  /**
   * Si está definido, `prepareStream` (sin `startTimeOverride`) usa este instante como ancla
   * en lugar de `selectedTime` — alinea la petición HLS con calendario + `<input type="time">`.
   */
  getRecordingSearchWallTime?: () => Date | null;
  /** Resuelve con la playlist o lanza Error (fallo del worker, timeout, etc.) */
  pollUntilCompleted?: (requestId: string) => Promise<{
    playlistUrl: string;
    startOffsetSeconds?: number;
    hlsWindowStart?: Date;
  }>;
  /**
   * Minutos de grabación *solo hacia atrás* desde el puntero: la petición va [ancla − preRoll, ancla + forward].
   * Da margen para retroceder en el mismo playlist sin nueva carga; no sustituye el instante solicitado (seek al ancla al Play).
   * Pre-roll: por defecto 3 min. Hacia adelante: `RECORDING_PLAYBACK_FORWARD_MINUTES` si no se pasa `playbackDurationMinutes` (tope `RECORDING_MAX_PLAYLIST_MINUTES`).
   */
  recordingPreRollMinutes?: number;
}

export interface UsePlayerStateResult {
  mode: PlayerMode;
  selectedTime: Date;
  slots: PlayerSlot[];
  timelinePosition: number;
  timelineWindowStart: Date;
  playbackStartTimeForDisplay: Date | null;
  masterElapsedSeconds: number;
  playbackSpeed: number;
  globalError: string | null;
  setGlobalError: (s: string | null) => void;
  /** Añadir cámara a un slot (drag & drop). Si PLAYING → prepareStream + startPlayback; si STOPPED → solo prepareStream */
  addCameraToSlot: (
    slotId: number,
    camera: PlayerSlot['camera'],
    prepareAt?: Date,
    /** Ventana HLS solicitada al API (por defecto RECORDING_PLAYBACK_FORWARD_MINUTES). Analíticas puede pasar `playbackMin` en URL. */
    playbackDurationMinutes?: number
  ) => Promise<void>;
  prepareStream: (
    slotId: number,
    cameraOverride?: PlayerSlot['camera'],
    startTimeOverride?: Date,
    playbackDurationMinutes?: number
  ) => Promise<boolean>;
  /** Quitar cámara del slot (destruye stream) */
  removeCameraFromSlot: (slotId: number) => void;
  /**
   * Play global. Si el estado es PAUSED continúa desde `video.currentTime`;
   * si es STOPPED arranca desde la fecha solicitada (ancla del usuario).
   */
  play: () => Promise<void>;
  /** Pause global: pausa todas las cámaras conservando `video.currentTime`. */
  pause: () => void;
  /** Stop global: pausa, lleva `video.currentTime` a 0 y deja el puntero en la fecha solicitada. */
  stop: () => void;
  /** Cambio en timeline (0..100). PLAYING → nuevo HLS + play; STOPPED → actualizar selectedTime + preparar */
  setTimelinePosition: (percent: number) => void;
  /**
   * Nuevo M3U8 en todos los slots visibles con cámara para este instante (p. ej. selector de hora).
   * A diferencia de `setTimelinePosition`, no usa seek local dentro de la ventana HLS ya cargada.
   */
  forceRecordingSlotsPrepareAtTime: (anchor: Date) => void;
  /**
   * Durante PLAYING: nuevo prepare desde el instante actual del reloj maestro (siguiente ventana según forward + cap).
   * Re-ancla el maestro como el roll automático (sin mover el puntero del timeline manualmente).
   */
  extendRecordingHlsWindowWhilePlaying: () => void;
  /**
   * Salto relativo (±N s) solo con seek local en el HLS ya cargado; no pide un nuevo M3U8.
   * `ok: false` cuando no hay ningún slot READY con playback o las ventanas no se solapan.
   */
  seekBySecondsLocal: (deltaSec: number) => SeekBySecondsLocalResult;
  setSelectedTime: (date: Date) => void;
  setPlaybackSpeed: (n: number) => void;
  registerPlayer: (slotId: number, ref: React.RefObject<PlaybackPlayerRef | null>) => void;
  unregisterPlayer: (slotId: number) => void;
  /** Posición actual del slot en segundos (para vista ampliada). 0 si no hay reproductor. */
  getSlotPlaybackTime: (slotId: number) => number;
  /**
   * Puntero al inicio de la(s) ventana(s) HLS ya cargada(s) + seek local (sin re-preparar M3U8).
   * Si no hay slot READY, solo ajusta el instante a inicio de día (sin `prepareStream`).
   */
  resetPointerToLoadedRecordingStart: () => void;
  /**
   * Pausa global, puntero al inicio de la ventana HLS ya cargada y seek local en cada tile.
   * No llama a `prepareStream` (no recarga M3U8).
   */
  stopAndResetToRecordingStart: () => void;
  /**
   * Extiene la ventana sin seek de sync (y el guard de ±N s) al remontar HLS, bajar velocidad
   * (hls.js stopLoad/startLoad) o volver del modal: evita bucle seek↔buffering con el intervalo de sync.
   */
  bumpHlsStabilityGuard: () => void;
}

function clampToWindow(t: Date, windowStart: Date, windowEnd: Date): Date {
  const ms = t.getTime();
  if (ms < windowStart.getTime()) return windowStart;
  if (ms > windowEnd.getTime()) return windowEnd;
  return t;
}

/**
 * Frame / puntero en grabaciones: si el timeline cae dentro de la ventana HLS preparada, se usa;
 * si no, el ancla del prepare (`timelineAnchorWallTime`). Solo si no hay ancla se usa el inicio del m3u8
 * (suele ser ~pre-roll antes del instante pedido: ahí el primer frame no coincide con la fecha elegida).
 */
export function effectiveRecordingAnchorWallTime(
  playback: SlotPlayback,
  selectedTime: Date
): Date {
  const lo = playback.startTime.getTime();
  const hi = playback.endTime.getTime();
  const s = selectedTime.getTime();
  if (s >= lo && s <= hi) {
    return new Date(s);
  }
  const a = playback.timelineAnchorWallTime;
  if (a != null && Number.isFinite(a.getTime())) {
    const t = a.getTime();
    if (t >= lo && t <= hi) return a;
    /** No devolver `startTime` (t≈0 del buffer / preroll): el ancla preparada es el instante buscado. */
    return clampToWindow(a, playback.startTime, playback.endTime);
  }
  return playback.startTime;
}

/**
 * El <video> usa currentTime del medio HLS; el ancla startOffsetSeconds alinea el inicio del playlist
 * con playback.startTime en la línea de tiempo de grabación (igual que seekTo en play/sync).
 */
export function wallClockMsFromPlaybackVideo(
  playback: SlotPlayback,
  videoCurrentTimeSec: number
): number {
  const anchor = playback.startOffsetSeconds ?? 0;
  const offsetSec = Math.max(0, videoCurrentTimeSec - anchor);
  return playback.startTime.getTime() + offsetSec * 1000;
}

/** Segundos desde el inicio de la ventana en tiempo de grabación (coherente con wallClockMsFromPlaybackVideo). */
export function recordingTimelineOffsetSec(
  playback: SlotPlayback,
  videoCurrentTimeSec: number
): number {
  const anchor = playback.startOffsetSeconds ?? 0;
  return Math.max(0, videoCurrentTimeSec - anchor);
}

export function usePlayerState(props: UsePlayerStateProps): UsePlayerStateResult {
  const {
    slotCount,
    selectedDate,
    selectedDateStr,
    token,
    requestPlaybackForCamera,
    requestBatchPlaybackForCameras,
    pollUntilCompleted,
    reportRecordingViewSession,
    recordingPreRollMinutes: recordingPreRollMinutesProp,
    onRecordingWindowNearEnd: onRecordingWindowNearEndProp,
    getRecordingSearchWallTime: getRecordingSearchWallTimeProp,
  } = props;
  const requestBatchPlaybackForCamerasRef = useRef(requestBatchPlaybackForCameras);
  requestBatchPlaybackForCamerasRef.current = requestBatchPlaybackForCameras;

  const recordingPreRollMinutes =
    recordingPreRollMinutesProp != null && Number.isFinite(recordingPreRollMinutesProp)
      ? Math.max(0, Math.min(120, Math.floor(Number(recordingPreRollMinutesProp))))
      : RECORDING_PRE_ROLL_MINUTES;
  const reportRecordingViewSessionRef = useRef(reportRecordingViewSession);
  reportRecordingViewSessionRef.current = reportRecordingViewSession;
  const onRecordingWindowNearEndRef = useRef(onRecordingWindowNearEndProp);
  onRecordingWindowNearEndRef.current = onRecordingWindowNearEndProp;
  const getRecordingSearchWallTimeRef = useRef(getRecordingSearchWallTimeProp);
  getRecordingSearchWallTimeRef.current = getRecordingSearchWallTimeProp;

  /** Evita repetir la oferta de extensión de ventana hasta que cambie el fin de la ventana HLS actual. */
  const nearEndExtendOfferSentForEndMsRef = useRef<number | null>(null);

  type RecordingViewMeta = {
    startedAtMs: number;
    requestId: string;
    siteId: number;
    cameraId: number;
    siteName?: string;
    cameraName?: string;
    playbackRangeStart: string;
    playbackRangeEnd: string;
  };

  const recordingViewMetaRef = useRef<Map<number, RecordingViewMeta>>(new Map());

  /** Último instante (Date.now()) en que un slot disparó prepare automático por borde de ventana HLS. */
  const rollPrepareCooldownRef = useRef<Map<number, number>>(new Map());

  /** Un solo re-anclaje del reloj maestro tras N rolls en paralelo (evita carreras entre slots). */
  const rollMasterReanchorTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const flushRecordingViewAuditForSlot = useCallback((slotId: number) => {
    const meta = recordingViewMetaRef.current.get(slotId);
    if (!meta) return;
    recordingViewMetaRef.current.delete(slotId);
    const durationSeconds = Math.max(0, Math.round((Date.now() - meta.startedAtMs) / 1000));
    const report = reportRecordingViewSessionRef.current;
    if (!report) return;
    void Promise.resolve(
      report({
        siteId: meta.siteId,
        cameraId: meta.cameraId,
        durationSeconds,
        requestId: meta.requestId,
        siteName: meta.siteName,
        cameraName: meta.cameraName,
        playbackRangeStart: meta.playbackRangeStart,
        playbackRangeEnd: meta.playbackRangeEnd
      })
    );
  }, []);

  const assignRecordingViewMeta = useCallback(
    (slotId: number, camera: PlayerSlot['camera'], playback: SlotPlayback) => {
      if (!camera) return;
      const siteIdNum = Number((camera as { siteId?: string; site?: { id?: string } }).siteId ?? (camera as { site?: { id?: string } }).site?.id);
      const cameraIdNum = Number((camera as { id: string }).id);
      if (!Number.isFinite(siteIdNum) || !Number.isFinite(cameraIdNum)) return;
      recordingViewMetaRef.current.set(slotId, {
        startedAtMs: Date.now(),
        requestId: playback.requestId,
        siteId: siteIdNum,
        cameraId: cameraIdNum,
        siteName: (camera as { site?: { name?: string } }).site?.name,
        cameraName: (camera as { name: string }).name,
        playbackRangeStart: playback.startTime.toISOString(),
        playbackRangeEnd: playback.endTime.toISOString()
      });
    },
    []
  );

  const n = Math.min(Math.max(1, slotCount), MAX_CAMERAS);
  const isVisibleSlot = useCallback((slotId: number) => slotId <= n, [n]);
  const [slots, setSlots] = useState<PlayerSlot[]>(() =>
    Array.from({ length: n }, (_, i) => ({
      id: i + 1,
      camera: null,
      streamStatus: 'IDLE' as StreamStatus,
    }))
  );
  const [mode, setMode] = useState<PlayerMode>('STOPPED');
  const [selectedTime, setSelectedTimeState] = useState(() => new Date());
  /** Siempre el último selectedTime — evita que prepareStream/play dependan del state y se recreen en cada tick/re-render (bucles HLS/buffering). */
  const selectedTimeRef = useRef(selectedTime);
  selectedTimeRef.current = selectedTime;

  const [playbackStartTimeForDisplay, setPlaybackStartTimeForDisplay] = useState<Date | null>(null);
  const [masterElapsedSeconds, setMasterElapsedSeconds] = useState(0);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [globalError, setGlobalError] = useState<string | null>(null);

  const masterWallStartRef = useRef<number>(0);
  const masterPlaybackStartRef = useRef<Date>(new Date(0));
  const playerRefsRef = useRef<Map<number, React.RefObject<PlaybackPlayerRef | null>>>(new Map());
  const slotsRef = useRef<PlayerSlot[]>([]);
  const syncIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const tickIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // modeRef mirrors the `mode` state so async callbacks always read current mode.
  const modeRef = useRef<PlayerMode>('STOPPED');
  // Generation counter per slot: each new prepareStream call increments the counter.
  // After every await the handler checks if it still holds the latest generation;
  // if not, a newer call has superseded it and the result is silently discarded.
  // Prevents stale responses from overwriting a newer playlist when dragging fast.
  const prepareGenRef = useRef<Map<number, number>>(new Map());
  // Debounce timer for timeline-driven re-preparation (drag produces many events/s).
  const timelineDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Tras play o salto en timeline: evita seeks de sincronización hasta que el buffer HLS se estabilice. */
  const syncWarmupUntilRef = useRef(0);
  /** Tras ±N s con seek local: el sync no debe corregir hasta que el vídeo asiente el seek (evita bucle / saltos). */
  const postLocalSeekSyncGuardUntilRef = useRef(0);
  /** Por slot: no encadenar seekTo cada tick mientras hls.js avanza el buffer (evita “TS” a tirones). */
  const syncSeekCooldownUntilRef = useRef<Map<number, number>>(new Map());
  /** Valor anterior de velocidad para re-anclar el reloj maestro al cambiar 0.5×↔4× sin bucle de seek. */
  const playbackSpeedPrevForAnchorRef = useRef(playbackSpeed);
  /**
   * Velocidad usada en tick/sync (integración wall×speed). NO actualizar en cada render con el nuevo
   * estado: si sube a 4× antes del efecto de re-ancla, elapsed usa 4× con reloj anclado a 1×/2× y
   * expectedSec salta → seek hacia atrás (“vuelve al inicio”).
   */
  const playbackSpeedRef = useRef(playbackSpeed);
  /** Última velocidad de la UI (sí se actualiza cada render) para leerla al pulsar Play. */
  const playbackSpeedLatestRef = useRef(playbackSpeed);
  playbackSpeedLatestRef.current = playbackSpeed;

  slotsRef.current = slots;
  modeRef.current = mode;

  const selectedDateRef = useRef(selectedDate);
  selectedDateRef.current = selectedDate;

  /** Misma medianoche del día; solo selectedDateStr en deps (sync “ahora” cambia selectedDate cada segundo sin cambiar el día). */
  const timelineWindowStart = useMemo(() => {
    const d = selectedDateRef.current;
    const str =
      selectedDateStr ||
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const parts = str.split('-').map(Number);
    if (parts.length !== 3 || !parts[0] || !parts[1] || !parts[2]) {
      return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
    }
    return new Date(parts[0], parts[1] - 1, parts[2], 0, 0, 0, 0);
  }, [selectedDateStr]);

  const [recordingTimelineClockMs, setRecordingTimelineClockMs] = useState(() => Date.now());

  /** Re-sincroniza el tope “hoy” al cambiar de día; tick periódico solo en el día actual. */
  useEffect(() => {
    setRecordingTimelineClockMs(Date.now());
  }, [timelineWindowStart]);

  useEffect(() => {
    if (!isSameLocalCalendarDay(timelineWindowStart, new Date())) return;
    const id = window.setInterval(() => setRecordingTimelineClockMs(Date.now()), 10_000);
    return () => window.clearInterval(id);
  }, [timelineWindowStart]);

  const timelineWindowEnd = useMemo(
    () => computeRecordingTimelineEffectiveEnd(timelineWindowStart, new Date(recordingTimelineClockMs)),
    [timelineWindowStart, recordingTimelineClockMs]
  );

  const timelineWindowStartRef = useRef(timelineWindowStart);
  const timelineWindowEndRef = useRef(timelineWindowEnd);
  timelineWindowStartRef.current = timelineWindowStart;
  timelineWindowEndRef.current = timelineWindowEnd;

  const setSelectedTime = useCallback((date: Date) => {
    const clamped = clampToWindow(date, timelineWindowStartRef.current, timelineWindowEndRef.current);
    // Evita que play()/stop() lean selectedTimeRef obsoleto hasta el siguiente render.
    selectedTimeRef.current = clamped;
    setSelectedTimeState(clamped);
  }, []);

  /** Si el tope del día (p. ej. “hoy”) retrocede con el reloj, recortar el puntero. */
  useEffect(() => {
    const twe = timelineWindowEnd.getTime();
    if (selectedTime.getTime() <= twe) return;
    /**
     * Solo recortar si `selectedTime` ya está dentro del mismo día calendario que `timelineWindowStart`.
     * Si están en días distintos (el usuario acaba de cambiar de día y `forceRecordingSlotsPrepareAtTime`
     * aún no actualizó el puntero), dejar que ese callback lo corrija; si recortamos aquí pisamos
     * la hora preservada y la UI queda a 23:59:59 en lugar del horario que eligió el usuario.
     */
    if (!isSameLocalCalendarDay(selectedTime, timelineWindowStart)) return;
    /**
     * `timelineWindowEnd` es el instante EXclusivo del fin del día (p. ej. medianoche del día siguiente).
     * Si aquí asignáramos ese valor al puntero, la UI muestra 00:00 / “12:00 a. m.” del día siguiente
     * aunque el calendario siga en el día anterior (p. ej. al pasar de “hoy” a ayer con la hora aún en el futuro).
     * Recortamos al último tick incluido dentro del día visible.
     */
    const tws = timelineWindowStart.getTime();
    const inclusiveEndMs = Math.max(tws, twe - 1);
    setSelectedTime(new Date(inclusiveEndMs));
  }, [timelineWindowEnd, selectedTime, timelineWindowStart, setSelectedTime]);

  const timelinePosition =
    selectedTime.getTime() <= timelineWindowStart.getTime()
      ? 0
      : selectedTime.getTime() >= timelineWindowEnd.getTime()
        ? 100
        : ((selectedTime.getTime() - timelineWindowStart.getTime()) / TIMELINE_MS) * 100;

  const registerPlayer = useCallback((slotId: number, ref: React.RefObject<PlaybackPlayerRef | null>) => {
    playerRefsRef.current.set(slotId, ref);
  }, []);
  const unregisterPlayer = useCallback((slotId: number) => {
    playerRefsRef.current.delete(slotId);
  }, []);

  /** Invalida cualquier prepareStream en vuelo para este slot (quitar cámara, mover a otro slot, etc.). */
  const bumpPrepareGeneration = useCallback((slotId: number) => {
    const cur = prepareGenRef.current.get(slotId) ?? 0;
    prepareGenRef.current.set(slotId, cur + 1);
  }, []);

  /**
   * Cola global de `prepareStream`: varias cámaras (p. ej. 2×2) encadenan POST/poll al motor sin solaparse,
   * para no saturar video-engine / NFS mientras las peticiones se procesan una tras otra.
   */
  const prepareStreamQueueTailRef = useRef<Promise<unknown>>(Promise.resolve());

  const getSlotPlaybackTime = useCallback((slotId: number): number => {
    const ref = playerRefsRef.current.get(slotId);
    const t = ref?.current?.getCurrentTime?.();
    return typeof t === 'number' && Number.isFinite(t) ? t : 0;
  }, []);

  const bumpHlsStabilityGuard = useCallback(() => {
    const now = Date.now();
    const warmupUntil = now + 10_000;
    const postSeekUntil = now + 9_000;
    syncWarmupUntilRef.current = Math.max(syncWarmupUntilRef.current, warmupUntil);
    postLocalSeekSyncGuardUntilRef.current = Math.max(
      postLocalSeekSyncGuardUntilRef.current,
      postSeekUntil
    );
    syncSeekCooldownUntilRef.current.clear();
  }, []);

  /**
   * Preparar stream para un slot: solicitar HLS al backend, actualizar a READY o ERROR.
   *
   * @param startTimeOverride  Usar esta hora en lugar de `selectedTime`.
   *   CRÍTICO para navegación en el timeline: `setSelectedTimeState` programa una
   *   actualización asíncrona de React, por lo que cuando `prepareStream` se llama
   *   inmediatamente después de setSelectedTimeState, `selectedTime` del closure aún
   *   tiene el valor viejo.  Pasar `newTime` explícitamente evita ese race.
   *
   * Generation counter: si llegan múltiples llamadas rápidas para el mismo slot
   * (drag del timeline), sólo la última aplica su resultado; las anteriores se
   * descartan en los puntos de control post-await.
   */
  const prepareStreamImpl = useCallback(
    async (
      slotId: number,
      cameraOverride?: PlayerSlot['camera'],
      startTimeOverride?: Date,
      playbackDurationMinutes?: number,
    ): Promise<boolean> => {
      const slot = slotsRef.current.find((s) => s.id === slotId);
      const camera = cameraOverride ?? slot?.camera;
      if (!camera) return false;

      const rawSiteId = (camera as any).siteId ?? (camera as any).site?.id;
      const siteId = Number(rawSiteId);
      const siteIdOk = Number.isFinite(siteId) && siteId > 0;

      if (!token) {
        const msg = 'Sesión no válida. Vuelve a iniciar sesión.';
        setSlots((prev) =>
          prev.map((s) => (s.id === slotId ? { ...s, streamStatus: 'ERROR' as StreamStatus, error: msg } : s))
        );
        setGlobalError(msg);
        return false;
      }
      if (!siteIdOk) {
        const msg =
          'No se puede solicitar la grabación: la cámara no tiene un sitio válido (siteId). Comprueba el inventario o vuelve a arrastrar la cámara desde el panel.';
        setSlots((prev) =>
          prev.map((s) => (s.id === slotId ? { ...s, streamStatus: 'ERROR' as StreamStatus, error: msg } : s))
        );
        return false;
      }

      flushRecordingViewAuditForSlot(slotId);
      unregisterPlayer(slotId);

      // Increment generation: any in-flight request with an older gen is now stale.
      const gen = (prepareGenRef.current.get(slotId) ?? 0) + 1;
      prepareGenRef.current.set(slotId, gen);
      const isStale = () => prepareGenRef.current.get(slotId) !== gen;

      setSlots((prev) =>
        prev.map((s) =>
          s.id === slotId
            ? {
                ...s,
                streamStatus: 'PREPARING' as StreamStatus,
                playback: undefined,
                error: undefined,
              }
            : s
        )
      );
      setGlobalError(null);

      // Ancla = instante en la línea de tiempo (puntero). La API pide [ancla − preRoll, ancla + forward]
      // Ventana hacia adelante: `RECORDING_PLAYBACK_FORWARD_MINUTES`. Total típico ≈ pre-roll atrás + forward desde el ancla (cap `RECORDING_MAX_PLAYLIST_MINUTES`).
      const ws = timelineWindowStartRef.current;
      const we = timelineWindowEndRef.current;
      const pickerWall = getRecordingSearchWallTimeRef.current?.() ?? null;
      const anchorBase =
        startTimeOverride != null
          ? startTimeOverride
          : pickerWall != null && Number.isFinite(pickerWall.getTime())
            ? pickerWall
            : selectedTimeRef.current;
      const anchorTime = clampToWindow(anchorBase, ws, we);
      const windowMinRaw =
        playbackDurationMinutes != null && Number.isFinite(playbackDurationMinutes)
          ? Math.floor(Number(playbackDurationMinutes))
          : RECORDING_PLAYBACK_FORWARD_MINUTES;
      const forwardMin = Math.min(RECORDING_MAX_PLAYLIST_MINUTES, Math.max(1, windowMinRaw));
      const preRollMs = recordingPreRollMinutes * 60 * 1000;
      const idealStart = new Date(anchorTime.getTime() - preRollMs);
      const idealEnd = new Date(anchorTime.getTime() + forwardMin * 60 * 1000);
      let startTime = clampToWindow(idealStart, ws, we);
      let endTime = clampToWindow(idealEnd, ws, we);
      /**
       * El backend limita cada playlist a `RECORDING_MAX_PLAYLIST_MINUTES` desde el *inicio solicitado*, no desde el ancla.
       * Con pre-roll + forward debe respetarse el cap del backend. Si guardamos `endTime` más allá del stream real,
       * play/±s/sync creen que el tiempo sigue "dentro del buffer" y el seek va al vacío → buffering perpetuo / reproducción rota.
       */
      const maxEndMs = startTime.getTime() + RECORDING_MAX_PLAYLIST_MINUTES * 60 * 1000;
      if (endTime.getTime() > maxEndMs) {
        endTime = clampToWindow(new Date(maxEndMs), ws, we);
      }
      if (endTime.getTime() <= startTime.getTime()) {
        endTime = new Date(Math.min(we.getTime(), startTime.getTime() + 60 * 1000));
      }

      try {
        const data = await requestPlaybackForCamera({
          siteId,
          cameraId: Number((camera as any).id),
          startTime,
          endTime,
          anchorTime,
        });

        if (isStale()) return false; // newer drag superseded this request

        let playlistUrl = data.playlistUrl ?? null;
        let startOffsetSeconds = data.startOffsetSeconds ?? 0;
        /** HLS t=0 = inicio del primer .ts (XTAM); alinea pill/reloj con el motor, no con el pre-roll “ideal”. */
        let hlsWindowStart: Date = data.startTime;
        if (!playlistUrl && pollUntilCompleted && data.requestId) {
          if (isStale()) return false;
          const polled = await pollUntilCompleted(data.requestId);
          if (isStale()) return false;
          playlistUrl = polled.playlistUrl;
          if (polled.startOffsetSeconds != null) startOffsetSeconds = polled.startOffsetSeconds;
          if (polled.hlsWindowStart) hlsWindowStart = polled.hlsWindowStart;
        }

        if (isStale()) return false;

        if (!playlistUrl) {
          setSlots((prev) =>
            prev.map((s) =>
              s.id === slotId ? { ...s, streamStatus: 'ERROR' as StreamStatus, error: 'Sin URL de playlist' } : s
            )
          );
          return false;
        }
        const playback: SlotPlayback = {
          requestId: data.requestId,
          playlistUrl,
          startTime: hlsWindowStart,
          endTime: data.endTime,
          startOffsetSeconds,
          timelineAnchorWallTime: anchorTime,
        };
        setSlots((prev) =>
          prev.map((s) => (s.id === slotId ? { ...s, streamStatus: 'READY' as StreamStatus, playback, error: undefined } : s))
        );
        /**
         * El puntero global (`selectedTime`) alimenta `effectiveRecordingAnchorWallTime` en todas las tiles.
         * Si cada `prepareStream` que termina hace `setSelectedTime(anchorTime)`, al cargar la 2ª–4ª cámara
         * se mueve el scrubber y todas las grabaciones reciben el mismo instante de ancla aunque cada
         * `playlistUrl`/cámara sea distinta. Solo re-sincronizamos el puntero cuando el prepare viene con
         * hora explícita (timeline, roll, play, `prepareAt`) o cuando es la primera cámara lista en la grilla.
         */
        const explicitTimeFromCaller = startTimeOverride != null;
        const anotherVisibleCameraAlreadyReady = slotsRef.current.some(
          (s) =>
            s.id !== slotId &&
            isVisibleSlot(s.id) &&
            s.streamStatus === 'READY' &&
            s.playback != null
        );
        if (explicitTimeFromCaller || !anotherVisibleCameraAlreadyReady) {
          setSelectedTime(anchorTime);
        }
        if (modeRef.current === 'PLAYING') {
          assignRecordingViewMeta(slotId, camera, playback);
        }
        return true;
      } catch (e: any) {
        if (isStale()) return false;
        const msg = e?.message ?? String(e);
        setSlots((prev) =>
          prev.map((s) => (s.id === slotId ? { ...s, streamStatus: 'ERROR' as StreamStatus, error: msg } : s))
        );
        setGlobalError(msg);
        return false;
      }
    },
    [
      token,
      requestPlaybackForCamera,
      pollUntilCompleted,
      flushRecordingViewAuditForSlot,
      unregisterPlayer,
      assignRecordingViewMeta,
      recordingPreRollMinutes,
      setSelectedTime,
      isVisibleSlot,
    ]
  );

  const prepareStream = useCallback(
    async (
      slotId: number,
      cameraOverride?: PlayerSlot['camera'],
      startTimeOverride?: Date,
      playbackDurationMinutes?: number,
    ): Promise<boolean> => {
      const run = prepareStreamQueueTailRef.current.then(() =>
        prepareStreamImpl(slotId, cameraOverride, startTimeOverride, playbackDurationMinutes)
      );
      prepareStreamQueueTailRef.current = run.catch(() => undefined);
      return run;
    },
    [prepareStreamImpl]
  );

  /**
   * Solicita HLS para varios slots a la vez usando el endpoint batch.
   * Omite la cola serial de prepareStream para que todas las cámaras reciban su m3u8 al mismo tiempo.
   * Solo se usa cuando `requestBatchPlaybackForCameras` está disponible.
   */
  const prepareBatchStreams = useCallback(
    async (slots: PlayerSlot[], startTimeOverride: Date): Promise<void> => {
      const batchFn = requestBatchPlaybackForCamerasRef.current;
      if (!batchFn || slots.length === 0) return;

      const ws = timelineWindowStartRef.current;
      const we = timelineWindowEndRef.current;

      type SlotMeta = {
        slotId: number;
        siteId: number;
        cameraId: number;
        startTime: Date;
        endTime: Date;
        anchorTime: Date;
      };

      const metas: SlotMeta[] = [];
      for (const slot of slots) {
        const camera = slot.camera;
        if (!camera) continue;
        const rawSiteId = (camera as any).siteId ?? (camera as any).site?.id;
        const siteId = Number(rawSiteId);
        if (!Number.isFinite(siteId) || siteId <= 0) continue;

        const anchorTime = clampToWindow(startTimeOverride, ws, we);
        const preRollMs = recordingPreRollMinutes * 60 * 1000;
        let startTime = clampToWindow(new Date(anchorTime.getTime() - preRollMs), ws, we);
        let endTime = clampToWindow(new Date(anchorTime.getTime() + RECORDING_PLAYBACK_FORWARD_MINUTES * 60 * 1000), ws, we);
        const maxEndMs = startTime.getTime() + RECORDING_MAX_PLAYLIST_MINUTES * 60 * 1000;
        if (endTime.getTime() > maxEndMs) endTime = clampToWindow(new Date(maxEndMs), ws, we);
        if (endTime.getTime() <= startTime.getTime()) {
          endTime = new Date(Math.min(we.getTime(), startTime.getTime() + 60 * 1000));
        }
        metas.push({ slotId: slot.id, siteId, cameraId: Number((camera as any).id), startTime, endTime, anchorTime });
      }

      if (metas.length === 0) return;

      // Marcar todos como PREPARING simultáneamente
      setSlots((prev) =>
        prev.map((s) =>
          metas.some((m) => m.slotId === s.id)
            ? { ...s, streamStatus: 'PREPARING' as StreamStatus, playback: undefined, error: undefined }
            : s
        )
      );

      let responses: BatchPlaybackResponse[];
      try {
        responses = await batchFn(metas.map((m) => ({
          slotId: m.slotId,
          siteId: m.siteId,
          cameraId: m.cameraId,
          startTime: m.startTime,
          endTime: m.endTime,
          anchorTime: m.anchorTime,
        })));
      } catch (e: any) {
        const msg = e?.message ?? 'Error al solicitar grabaciones';
        setSlots((prev) =>
          prev.map((s) =>
            metas.some((m) => m.slotId === s.id)
              ? { ...s, streamStatus: 'ERROR' as StreamStatus, error: msg }
              : s
          )
        );
        setGlobalError(msg);
        return;
      }

      // Actualizar todos los slots al mismo tiempo con sus playlistUrls
      setSlots((prev) =>
        prev.map((s) => {
          const resp = responses.find((r) => r.slotId === s.id);
          if (!resp) return s;
          const meta = metas.find((m) => m.slotId === s.id);
          if (resp.error || !resp.playlistUrl) {
            return { ...s, streamStatus: 'ERROR' as StreamStatus, error: resp.error ?? 'Sin URL de playlist' };
          }
          const playback: SlotPlayback = {
            requestId: resp.requestId,
            playlistUrl: resp.playlistUrl,
            startTime: resp.startTime,
            endTime: resp.endTime,
            startOffsetSeconds: resp.startOffsetSeconds ?? 0,
            timelineAnchorWallTime: meta?.anchorTime,
          };
          return { ...s, streamStatus: 'READY' as StreamStatus, playback, error: undefined };
        })
      );
    },
    [recordingPreRollMinutes]
  );

  const scheduleMasterReanchorAfterRecordingRoll = useCallback(() => {
    if (rollMasterReanchorTimerRef.current) {
      clearTimeout(rollMasterReanchorTimerRef.current);
    }
    rollMasterReanchorTimerRef.current = setTimeout(() => {
      rollMasterReanchorTimerRef.current = null;
      if (modeRef.current !== 'PLAYING') return;
      const wallNow = Date.now();
      const spd = playbackSpeedRef.current;
      const el = ((wallNow - masterWallStartRef.current) / 1000) * spd;
      const recordingWallNow = new Date(masterPlaybackStartRef.current.getTime() + el * 1000);
      masterPlaybackStartRef.current = recordingWallNow;
      masterWallStartRef.current = wallNow;
      setPlaybackStartTimeForDisplay(recordingWallNow);
      setMasterElapsedSeconds(0);
      syncWarmupUntilRef.current = wallNow + 8000;
      syncSeekCooldownUntilRef.current.clear();
      postLocalSeekSyncGuardUntilRef.current = Math.max(
        postLocalSeekSyncGuardUntilRef.current,
        wallNow + 7000
      );
      bumpHlsStabilityGuard();
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const spd2 = playbackSpeedRef.current;
          const el2 = ((Date.now() - masterWallStartRef.current) / 1000) * spd2;
          const mas = masterPlaybackStartRef.current.getTime() / 1000 + el2;
          playerRefsRef.current.forEach((ref, slotId) => {
            const pb = slotsRef.current.find((sl) => sl.id === slotId)?.playback;
            if (!pb || !ref?.current) return;
            const anch = pb.startOffsetSeconds ?? 0;
            let exp = anch + Math.max(0, mas - pb.startTime.getTime() / 1000);
            const dur = ref.current.getDuration?.() ?? 0;
            if (dur > 1 && Number.isFinite(dur)) {
              exp = Math.min(exp, Math.max(anch, dur - 0.35));
            }
            ref.current.seekTo(exp, { skipIfClose: false });
          });
        });
      });
    }, 160);
  }, [bumpHlsStabilityGuard]);

  const extendRecordingHlsWindowWhilePlaying = useCallback(() => {
    if (modeRef.current !== 'PLAYING') return;
    nearEndExtendOfferSentForEndMsRef.current = null;
    const speed = playbackSpeedRef.current;
    const elapsed = ((Date.now() - masterWallStartRef.current) / 1000) * speed;
    const masterAbsSec = masterPlaybackStartRef.current.getTime() / 1000 + elapsed;
    const curWallMs = masterAbsSec * 1000;
    const wsWall = timelineWindowStartRef.current.getTime();
    const weWall = timelineWindowEndRef.current.getTime();
    let anchorMs = curWallMs;
    if (anchorMs < wsWall) anchorMs = wsWall;
    if (anchorMs > weWall - 500) anchorMs = weWall - 500;
    const anchorDate = new Date(anchorMs);
    bumpHlsStabilityGuard();
    const freshSlots = slotsRef.current;
    for (const s of freshSlots) {
      if (!isVisibleSlot(s.id)) continue;
      if (s.streamStatus !== 'READY') continue;
      if (!s.camera || !s.playback) continue;
      void prepareStream(s.id, s.camera, anchorDate).then((ok) => {
        if (!ok || modeRef.current !== 'PLAYING') return;
        bumpHlsStabilityGuard();
        scheduleMasterReanchorAfterRecordingRoll();
      });
    }
  }, [prepareStream, isVisibleSlot, bumpHlsStabilityGuard, scheduleMasterReanchorAfterRecordingRoll]);

  const forceRecordingSlotsPrepareAtTime = useCallback(
    (anchor: Date) => {
      const ws = timelineWindowStartRef.current;
      const we = timelineWindowEndRef.current;
      const clamped = clampToWindow(anchor, ws, we);
      if (timelineDebounceRef.current) {
        clearTimeout(timelineDebounceRef.current);
        timelineDebounceRef.current = null;
      }
      selectedTimeRef.current = clamped;
      setSelectedTimeState(clamped);

      const slotsToPrep = slotsRef.current.filter(
        (s) =>
          isVisibleSlot(s.id) &&
          s.camera != null &&
          (s.streamStatus === 'READY' ||
            s.streamStatus === 'PREPARING' ||
            s.streamStatus === 'ERROR')
      );
      slotsToPrep.forEach((s) => {
        void prepareStream(s.id, undefined, clamped);
      });
    },
    [prepareStream, isVisibleSlot]
  );

  /**
   * Añadir cámara a slot — implementa la Regla Central del reproductor:
   *
   *   STOPPED → preparar stream (IDLE → PREPARING → READY), NO reproducir.
   *             isPlaying=false se propaga via prop; CameraVideoPlayer no llamará play().
   *             Al presionar Play global, todas las READY arrancan sincronizadas.
   *
   *   PLAYING → preparar stream; una vez READY, isPlaying=true se propaga via prop.
   *             NO se resetea el master clock (haría desincronizar cámaras existentes).
   *             El sync interval (500ms) hace el seek inicial; también lo hacemos de
   *             forma inmediata aquí para reducir ese gap al mínimo.
   */
  const addCameraToSlot = useCallback(
    async (
      slotId: number,
      camera: PlayerSlot['camera'],
      prepareAt?: Date,
      playbackDurationMinutes?: number,
    ) => {
      if (!camera) return;
      const cameraId = (camera as any).id;
      const currentMode = modeRef.current;
      const origin: SlotOrigin = currentMode === 'PLAYING' ? 'added_while_playing' : 'added_while_stopped';

      const duplicateSlotIds = slotsRef.current
        .filter((s) => s.camera != null && String((s.camera as any).id) === String(cameraId) && s.id !== slotId)
        .map((s) => s.id);
      duplicateSlotIds.forEach((id) => {
        bumpPrepareGeneration(id);
        unregisterPlayer(id);
      });

      setSlots((prev) =>
        prev.map((s) => {
          // Si la cámara ya estaba en otro slot, limpiar ese slot
          if (s.camera && (s.camera as any).id === cameraId && s.id !== slotId) {
            flushRecordingViewAuditForSlot(s.id);
            return { id: s.id, camera: null, streamStatus: 'IDLE' as StreamStatus };
          }
          if (s.id === slotId) {
            return { ...s, camera, streamStatus: 'PREPARING' as StreamStatus, playback: undefined, error: undefined, origin };
          }
          return s;
        })
      );

      const ok = await prepareStream(slotId, camera, prepareAt, playbackDurationMinutes);

      if (ok && modeRef.current === 'PLAYING') {
        // Seek inmediato al tiempo actual del master para minimizar el gap antes de
        // que el sync interval (500ms) haga su primera corrección.
        // NO se toca masterPlaybackStartRef ni masterWallStartRef — eso desincronizaría
        // las cámaras que ya están reproduciendo.
        const speed = playbackSpeedRef.current;
        const elapsed = ((Date.now() - masterWallStartRef.current) / 1000) * speed;
        const masterAbsSec = masterPlaybackStartRef.current.getTime() / 1000 + elapsed;
        const slotPlayback = slotsRef.current.find((s) => s.id === slotId)?.playback;
        if (slotPlayback) {
          const slotStartSec = slotPlayback.startTime.getTime() / 1000;
          const anchor = slotPlayback.startOffsetSeconds ?? 0;
          const seekTarget = anchor + Math.max(0, masterAbsSec - slotStartSec);
          // El ref puede no estar registrado aún (CameraDropZone lo registra en el efecto
          // siguiente al render). Reintentamos una vez tras el ciclo de renderizado.
          const trySeek = () => {
            const ref = playerRefsRef.current.get(slotId);
            if (ref?.current) {
              ref.current.seekTo(seekTarget, { skipIfClose: true });
            }
          };
          trySeek();
          // Segundo intento tras el render en caso de que el ref no estuviera listo aún
          setTimeout(trySeek, 50);
        }
      }
      // STOPPED: stream listo con isPlaying=false — CameraVideoPlayer no reproduce (Fix 1)
    },
    [prepareStream, bumpPrepareGeneration, unregisterPlayer,flushRecordingViewAuditForSlot]
  );

  const removeCameraFromSlot = useCallback(
    (slotId: number) => {
      flushRecordingViewAuditForSlot(slotId);
      bumpPrepareGeneration(slotId);
      setSlots((prev) =>
        prev.map((s) =>
          s.id === slotId ? { id: s.id, camera: null, streamStatus: 'IDLE' as StreamStatus } : s
        )
      );
      unregisterPlayer(slotId);
    },
    [bumpPrepareGeneration, unregisterPlayer,flushRecordingViewAuditForSlot]
  );

  /**
   * Detiene los bucles de reproducción (reloj maestro, sync multi-cámara, roll de ventana)
   * y cancela el debounce de timeline pendiente. Común a `pause()` y `stop()`.
   */
  const teardownPlaybackLoops = useCallback(() => {
    if (rollMasterReanchorTimerRef.current) {
      clearTimeout(rollMasterReanchorTimerRef.current);
      rollMasterReanchorTimerRef.current = null;
    }
    nearEndExtendOfferSentForEndMsRef.current = null;
    if (syncIntervalRef.current) {
      clearInterval(syncIntervalRef.current);
      syncIntervalRef.current = null;
    }
    if (tickIntervalRef.current) {
      clearInterval(tickIntervalRef.current);
      tickIntervalRef.current = null;
    }
    syncSeekCooldownUntilRef.current.clear();
    postLocalSeekSyncGuardUntilRef.current = 0;
    // Tras parar, el HLS y el reloj maestro pueden desalinearse un instante: evita seek de sync inmediato.
    bumpHlsStabilityGuard();
    // Cancela cualquier re-preparación de timeline en debounce para que no dispare después.
    if (timelineDebounceRef.current) {
      clearTimeout(timelineDebounceRef.current);
      timelineDebounceRef.current = null;
    }
  }, [bumpHlsStabilityGuard]);

  /**
   * Pause: pausa la reproducción conservando `video.currentTime` en cada cámara.
   * No mueve el puntero al inicio ni destruye el HLS; el siguiente `play()` continúa
   * exactamente desde el mismo frame. Si no se está reproduciendo, solo asegura el pause.
   */
  const pause = useCallback(() => {
    if (modeRef.current !== 'PLAYING') {
      playerRefsRef.current.forEach((ref) => {
        try {
          ref?.current?.pause?.();
        } catch {
          /* ignore */
        }
      });
      return;
    }
    teardownPlaybackLoops();
    Array.from(recordingViewMetaRef.current.keys()).forEach((id) => {
      flushRecordingViewAuditForSlot(id);
    });
    // Anclar selectedTime al frame REAL del video (no al reloj maestro proyectado).
    // El maestro adelanta el video por buffering — usarlo deja un desfase de 1-2 s entre el
    // timestamp mostrado y el frame visible, que se acumula al pulsar retroceder/avanzar.
    let pausedAtMs: number | null = null;
    const readySlot = slotsRef.current.find(
      (s) => isVisibleSlot(s.id) && s.streamStatus === 'READY' && s.playback
    );
    if (readySlot) {
      const ref = playerRefsRef.current.get(readySlot.id);
      const currentSec = ref?.current?.getCurrentTime?.();
      if (typeof currentSec === 'number' && Number.isFinite(currentSec) && currentSec > 0) {
        const pb = readySlot.playback!;
        const offset = pb.startOffsetSeconds ?? 0;
        pausedAtMs = pb.startTime.getTime() + Math.max(0, currentSec - offset) * 1000;
      }
    }
    if (pausedAtMs == null) {
      const wallStart = masterWallStartRef.current;
      const playStart = masterPlaybackStartRef.current;
      if (wallStart > 0 && Number.isFinite(playStart.getTime()) && playStart.getTime() !== 0) {
        const elapsedSec = ((Date.now() - wallStart) / 1000) * playbackSpeedRef.current;
        pausedAtMs = playStart.getTime() + elapsedSec * 1000;
      }
    }
    if (pausedAtMs != null) {
      setSelectedTime(
        clampToWindow(
          new Date(pausedAtMs),
          timelineWindowStartRef.current,
          timelineWindowEndRef.current
        )
      );
    }
    // Pausa explícita en TODAS las cámaras registradas (no solo READY): evita el caso en que
    // un slot mantenía playback porque su prop `isPlaying` dependía de streamStatus.
    playerRefsRef.current.forEach((ref) => {
      try {
        ref?.current?.pause?.();
      } catch {
        /* ignore */
      }
    });
    setMode('PAUSED');
    modeRef.current = 'PAUSED';
    setPlaybackStartTimeForDisplay(null);
    setMasterElapsedSeconds(0);
  }, [flushRecordingViewAuditForSlot, isVisibleSlot, setSelectedTime, teardownPlaybackLoops]);

  /**
   * Stop: pausa, lleva `video.currentTime` a 0 en cada cámara y deja el puntero en la
   * fecha solicitada por el usuario (el ancla elegida). No destruye el HLS — el siguiente
   * `play()` vuelve a arrancar desde esa fecha sin recargar el playlist.
   */
  const stop = useCallback(() => {
    teardownPlaybackLoops();
    Array.from(recordingViewMetaRef.current.keys()).forEach((id) => {
      flushRecordingViewAuditForSlot(id);
    });
    // Pausar todas las cámaras y dejar el <video> en el ANCLA solicitada (la fecha elegida por el
    // usuario), NO en t=0 del playlist (que es ~pre-roll antes). Como play() ya no re-seeka, el
    // vídeo debe quedar parado exactamente donde el reloj maestro dirá que está al volver a Play;
    // rebobinar a 0 dejaría el frame en el pre-roll y la hora mostrada desfasada.
    playerRefsRef.current.forEach((ref) => {
      try {
        ref?.current?.pause?.();
      } catch {
        /* ignore */
      }
    });
    slotsRef.current.forEach((s) => {
      if (!isVisibleSlot(s.id) || s.streamStatus !== 'READY' || !s.playback) return;
      const pb = s.playback;
      const a = pb.timelineAnchorWallTime;
      const anchorWallMs =
        a != null && Number.isFinite(a.getTime()) ? a.getTime() : pb.startTime.getTime();
      const seekSec =
        (pb.startOffsetSeconds ?? 0) +
        Math.max(0, (anchorWallMs - pb.startTime.getTime()) / 1000);
      try {
        playerRefsRef.current.get(s.id)?.current?.seekTo?.(seekSec);
      } catch {
        /* ignore */
      }
    });
    setMode('STOPPED');
    modeRef.current = 'STOPPED';
    setPlaybackStartTimeForDisplay(null);
    setMasterElapsedSeconds(0);
    // Puntero a la fecha solicitada (ancla del usuario), NO al inicio del playlist HLS:
    // el m3u8 arranca ~5 min antes por el preroll. Así el siguiente Play arranca desde
    // lo que pidió el usuario, no desde el preroll.
    const ready = slotsRef.current.filter(
      (s) => isVisibleSlot(s.id) && s.camera && s.streamStatus === 'READY' && s.playback
    );
    if (ready.length > 0) {
      const anchorMs = Math.min(
        ...ready.map((s) => {
          const pb = s.playback!;
          const a = pb.timelineAnchorWallTime;
          return a != null && Number.isFinite(a.getTime()) ? a.getTime() : pb.startTime.getTime();
        })
      );
      setSelectedTime(
        clampToWindow(
          new Date(anchorMs),
          timelineWindowStartRef.current,
          timelineWindowEndRef.current
        )
      );
    }
  }, [flushRecordingViewAuditForSlot, isVisibleSlot, setSelectedTime, teardownPlaybackLoops]);

  /**
   * Reloj maestro derivado del vídeo REAL: hora de grabación (ms) de la 1.ª cámara visible
   * READY cuyo <video> ya tiene un `currentTime` válido. Devuelve null si ninguna lo tiene aún
   * (recién montadas / buffering inicial), y entonces el tick cae al respaldo wall×velocidad.
   */
  const readMasterRecordingWallMsFromVideo = useCallback((): number | null => {
    for (const s of slotsRef.current) {
      if (!isVisibleSlot(s.id)) continue;
      if (s.streamStatus !== 'READY' || !s.playback) continue;
      const cur = playerRefsRef.current.get(s.id)?.current?.getCurrentTime?.();
      if (typeof cur === 'number' && Number.isFinite(cur) && cur > 0) {
        return wallClockMsFromPlaybackVideo(s.playback, cur);
      }
    }
    return null;
  }, [isVisibleSlot]);

  /**
   * Arranca el reloj maestro (tick) y el bucle de sincronización multi-cámara / roll de ventana.
   * Compartido por el arranque desde STOPPED y la reanudación desde PAUSED.
   */
  const startPlaybackLoops = useCallback(() => {
    // Limpia intervalos previos para evitar duplicados si se reentra (navegación de timeline en PLAYING).
    if (syncIntervalRef.current) clearInterval(syncIntervalRef.current);
    if (tickIntervalRef.current) clearInterval(tickIntervalRef.current);

    const playGuardsAt = Date.now();
    syncWarmupUntilRef.current = playGuardsAt + 8000;
    syncSeekCooldownUntilRef.current.clear();
    postLocalSeekSyncGuardUntilRef.current = 0;

    // Reloj maestro: se calcula desde el `currentTime` REAL de la cámara de referencia, no se
    // proyecta wall×velocidad. Así la hora mostrada sigue exactamente al frame visible y, si el
    // vídeo bufferiza, el reloj se detiene con él en vez de adelantarse. Respaldo wall×velocidad
    // solo mientras ninguna cámara tiene todavía un `currentTime` válido.
    tickIntervalRef.current = setInterval(() => {
      const videoWallMs = readMasterRecordingWallMsFromVideo();
      if (videoWallMs != null) {
        const elapsed = (videoWallMs - masterPlaybackStartRef.current.getTime()) / 1000;
        setMasterElapsedSeconds(Math.max(0, elapsed));
        return;
      }
      const speed = playbackSpeedRef.current;
      const elapsed = ((Date.now() - masterWallStartRef.current) / 1000) * speed;
      setMasterElapsedSeconds(Math.max(0, elapsed));
    }, 250);

    syncIntervalRef.current = setInterval(() => {
      if (Date.now() < syncWarmupUntilRef.current) return;
      if (Date.now() < postLocalSeekSyncGuardUntilRef.current) return;
      const speed = playbackSpeedRef.current;
      const elapsed = ((Date.now() - masterWallStartRef.current) / 1000) * speed;
      const masterAbsSec = masterPlaybackStartRef.current.getTime() / 1000 + elapsed;
      const currentSlots = slotsRef.current;
      const nowMs = Date.now();

      if (RECORDING_ENFORCE_MULTICAM_TIMELINE_DRIFT_SYNC) {
        playerRefsRef.current.forEach((ref, slotId) => {
          const slotData = currentSlots.find((s) => s.id === slotId)?.playback;
          if (!slotData || !ref.current) return;
          if (ref.current.isBuffering?.()) return;
          const slotStartSec = slotData.startTime.getTime() / 1000;
          const anchor = slotData.startOffsetSeconds ?? 0;
          let expectedSec = anchor + Math.max(0, masterAbsSec - slotStartSec);
          const dur = ref.current.getDuration?.() ?? 0;
          if (dur > 1 && Number.isFinite(dur)) {
            expectedSec = Math.min(expectedSec, Math.max(anchor, dur - 0.25));
          }
          const current = ref.current.getCurrentTime();
          const drift = Math.abs(current - expectedSec);
          if (drift <= SYNC_TOLERANCE_SEC) return;
          const coolUntil = syncSeekCooldownUntilRef.current.get(slotId) ?? 0;
          if (nowMs < coolUntil && drift < SYNC_FORCE_SEEK_DRIFT_SEC) return;
          ref.current.seekTo(expectedSec);
          syncSeekCooldownUntilRef.current.set(slotId, nowMs + SYNC_SEEK_COOLDOWN_MS);
        });
      }

      // Extensión de ventana HLS durante PLAYING (opcional): con RECORDING_AUTO_EXTEND_PLAYLIST_WHILE_PLAYING
      // en false no se encadenan prepares automáticos; la ventana configurada se usa en una sola carga.
      if (RECORDING_AUTO_EXTEND_PLAYLIST_WHILE_PLAYING && modeRef.current === 'PLAYING') {
        const freshSlots = slotsRef.current;
        if (!freshSlots.some((s) => isVisibleSlot(s.id) && s.streamStatus === 'PREPARING')) {
          const curWallMs = masterAbsSec * 1000;
          const weWall = timelineWindowEndRef.current.getTime();
          if (curWallMs < weWall - 15_000) {
            for (const s of freshSlots) {
              if (!isVisibleSlot(s.id)) continue;
              if (s.streamStatus !== 'READY') continue;
              if (!s.camera || !s.playback) continue;
              const endMs = s.playback.endTime.getTime();
              const remainingMs = endMs - curWallMs;
              const rollThresholdMs = recordingRollRemainingThresholdMs(s.playback);
              if (remainingMs > rollThresholdMs) continue;
              const lastRoll = rollPrepareCooldownRef.current.get(s.id) ?? 0;
              if (nowMs - lastRoll < ROLL_PREPARE_COOLDOWN_MS) continue;
              rollPrepareCooldownRef.current.set(s.id, nowMs);
              bumpHlsStabilityGuard();
              const wsWall = timelineWindowStartRef.current.getTime();
              let anchorMs = curWallMs;
              if (anchorMs < wsWall) anchorMs = wsWall;
              if (anchorMs > weWall - 500) anchorMs = weWall - 500;
              void prepareStream(s.id, s.camera, new Date(anchorMs)).then((ok) => {
                if (!ok || modeRef.current !== 'PLAYING') return;
                bumpHlsStabilityGuard();
                scheduleMasterReanchorAfterRecordingRoll();
              });
            }
          }
        }
      } else if (!RECORDING_AUTO_EXTEND_PLAYLIST_WHILE_PLAYING && modeRef.current === 'PLAYING') {
        const cb = onRecordingWindowNearEndRef.current;
        if (cb) {
          const freshSlots = slotsRef.current;
          if (!freshSlots.some((s) => isVisibleSlot(s.id) && s.streamStatus === 'PREPARING')) {
            const curWallMs = masterAbsSec * 1000;
            const weWall = timelineWindowEndRef.current.getTime();
            if (curWallMs < weWall - 15_000) {
              // Solo el tramo final (p. ej. último ~1 min de forward), no el umbral de auto-roll (~3–5 min).
              const offerThresholdMs = recordingNearEndOfferRemainingThresholdMs();
              let windowEndKey: number | null = null;
              for (const s of freshSlots) {
                if (!isVisibleSlot(s.id)) continue;
                if (s.streamStatus !== 'READY') continue;
                if (!s.camera || !s.playback) continue;
                const endMs = s.playback.endTime.getTime();
                const remainingMs = endMs - curWallMs;
                if (remainingMs <= offerThresholdMs && remainingMs > -10_000) {
                  windowEndKey = endMs;
                  break;
                }
              }
              if (
                windowEndKey != null &&
                nearEndExtendOfferSentForEndMsRef.current !== windowEndKey
              ) {
                nearEndExtendOfferSentForEndMsRef.current = windowEndKey;
                cb({ windowEndMs: windowEndKey });
              }
            }
          }
        }
      }
    }, SYNC_INTERVAL_MS);
  }, [
    prepareStream,
    isVisibleSlot,
    bumpHlsStabilityGuard,
    scheduleMasterReanchorAfterRecordingRoll,
    readMasterRecordingWallMsFromVideo,
  ]);

  const play = useCallback(async () => {
    // --- RESUME desde PAUSED: continuar exactamente desde `video.currentTime` ---
    // No se re-seeka ni se re-prepara: solo se re-arranca el reloj maestro anclado
    // al frame donde quedó la pausa y se reanuda la reproducción.
    if (modeRef.current === 'PAUSED') {
      const resumeSlots = slotsRef.current.filter(
        (s) => isVisibleSlot(s.id) && s.streamStatus === 'READY' && s.playback
      );
      if (resumeSlots.length === 0) {
        setGlobalError('Añade al menos una cámara y selecciona una hora');
        return;
      }
      // Anclar el reloj maestro al frame REAL donde quedó la pausa (no al reloj proyectado).
      let resumeWallMs: number | null = null;
      const refSlot = resumeSlots[0]!;
      const refRef = playerRefsRef.current.get(refSlot.id);
      const curSec = refRef?.current?.getCurrentTime?.();
      if (typeof curSec === 'number' && Number.isFinite(curSec) && curSec > 0) {
        const pb = refSlot.playback!;
        const offset = pb.startOffsetSeconds ?? 0;
        resumeWallMs = pb.startTime.getTime() + Math.max(0, curSec - offset) * 1000;
      }
      const resumeAt = clampToWindow(
        resumeWallMs != null ? new Date(resumeWallMs) : selectedTimeRef.current,
        timelineWindowStartRef.current,
        timelineWindowEndRef.current
      );
      masterPlaybackStartRef.current = resumeAt;
      masterWallStartRef.current = Date.now();
      setPlaybackStartTimeForDisplay(resumeAt);
      playbackSpeedRef.current = playbackSpeedLatestRef.current;
      setMasterElapsedSeconds(0);

      setMode('PLAYING');
      modeRef.current = 'PLAYING';

      await Promise.all(
        Array.from(playerRefsRef.current.values()).map(
          (ref) => ref.current?.play().catch(() => {/* autoplay bloqueado → efecto isPlaying */}) ?? Promise.resolve()
        )
      );

      resumeSlots.forEach((s) => {
        if (s.camera && s.playback) assignRecordingViewMeta(s.id, s.camera, s.playback);
      });

      startPlaybackLoops();
      return;
    }

    const ws = timelineWindowStartRef.current;
    const we = timelineWindowEndRef.current;
    const pickerWall = getRecordingSearchWallTimeRef.current?.() ?? null;
    const sel = selectedTimeRef.current;
    const pickerOk = pickerWall != null && Number.isFinite(pickerWall.getTime());

    const wallInsideAnyReadyHls = (wall: Date) =>
      slotsRef.current.some(
        (s) =>
          isVisibleSlot(s.id) &&
          s.streamStatus === 'READY' &&
          s.playback != null &&
          wall.getTime() >= s.playback.startTime.getTime() &&
          wall.getTime() <= s.playback.endTime.getTime()
      );

    const selInsideAnyHlsWindow = wallInsideAnyReadyHls(sel);
    const pickerInsideAnyHlsWindow = pickerOk && wallInsideAnyReadyHls(pickerWall!);

    /**
     * - Picker fuera del m3u8 y puntero dentro: el usuario buscó otra hora (calendario + input) distinta del
     *   buffer cargado → usar el picker, no quedarse en la hora del puntero al pulsar Play.
     * - Ambos dentro del mismo playlist: priorizar el puntero (scrub / pausa en un frame) frente al input
     *   que puede no haberse alineado aún con el timeline.
     * - Sin picker válido: mantener `selectedTime`.
     */
    const baseWall =
      pickerOk && !pickerInsideAnyHlsWindow && selInsideAnyHlsWindow
        ? pickerWall!
        : selInsideAnyHlsWindow && (!pickerOk || pickerInsideAnyHlsWindow)
          ? sel
          : pickerOk
            ? pickerWall!
            : sel;
    let startTime = clampToWindow(baseWall, ws, we);
    if (Math.abs(startTime.getTime() - selectedTimeRef.current.getTime()) > 500) {
      selectedTimeRef.current = startTime;
      setSelectedTimeState(startTime);
    }

    /**
     * El timeline puede desalinearse del HLS prep (p. ej. "sync con ahora" pone selectedTime al segundo
     * actual cada 1s mientras el stream sigue anclado al evento). El primer fotograma es correcto
     * pero play() ve selectedTime fuera de [startTime,endTime] y re-prepara sin necesidad.
     * Si el ancla guardada en el slot sigue dentro de la ventana HLS **y** el objetivo está cerca en
     * tiempo de esa ancla, usamos la ancla y alineamos el puntero. Saltos grandes = nueva hora buscada:
     * no snap (se re-prepara más abajo).
     */
    if (modeRef.current === 'STOPPED') {
      const ready = slotsRef.current.filter(
        (s) => isVisibleSlot(s.id) && s.streamStatus === 'READY' && s.playback
      );
      for (const s of ready) {
        const pb = s.playback!;
        const t = startTime.getTime();
        const h0 = pb.startTime.getTime();
        const h1 = pb.endTime.getTime();
        const anchor = pb.timelineAnchorWallTime;
        if (anchor == null || !Number.isFinite(anchor.getTime())) continue;
        const a = anchor.getTime();
        if (
          (t < h0 || t > h1) &&
          a >= h0 &&
          a <= h1 &&
          Math.abs(t - a) <= RECORDING_PLAY_SNAP_TO_ANCHOR_MAX_DRIFT_MS
        ) {
          const snapped = clampToWindow(anchor, ws, we);
          startTime = snapped;
          selectedTimeRef.current = snapped;
          setSelectedTime(snapped);
          break;
        }
      }
    }

    // Check if any READY slot's HLS window no longer covers the selected time.
    // This happens when: (1) the user navigated the timeline in STOPPED mode and
    // the debounce hasn't fired yet, or (2) the debounce fired but re-preparation
    // is still in flight. In that case we must re-prepare before playing; otherwise
    // seekTo() would target a position outside the HLS duration → buffering forever.
    const slotsNeedingReprepare = slotsRef.current.filter((s) => {
      if (!isVisibleSlot(s.id)) return false;
      if (!s.camera || s.streamStatus !== 'READY' || !s.playback) return false;
      const hlsStart = s.playback.startTime.getTime();
      const hlsEnd = s.playback.endTime.getTime();
      const target = startTime.getTime();
      return target < hlsStart || target > hlsEnd;
    });

    if (slotsNeedingReprepare.length > 0) {
      // Re-prepare out-of-range slots before starting playback.
      // Cancel any pending debounce so we don't double-prepare.
      if (timelineDebounceRef.current) {
        clearTimeout(timelineDebounceRef.current);
        timelineDebounceRef.current = null;
      }
      if (requestBatchPlaybackForCamerasRef.current && slotsNeedingReprepare.length > 1) {
        // Solicitar todas las cámaras al mismo tiempo con una sola llamada batch
        await prepareBatchStreams(slotsNeedingReprepare, startTime);
      } else {
        for (const s of slotsNeedingReprepare) {
          await prepareStream(s.id, undefined, startTime);
        }
      }
    }

    const readySlots = slotsRef.current.filter((s) => isVisibleSlot(s.id) && s.streamStatus === 'READY' && s.playback);
    if (readySlots.length === 0) {
      setGlobalError('Añade al menos una cámara y selecciona una hora');
      return;
    }

    // Tras await prepareStream, setSelectedTime(anchor) pudo quedar aún no reflejado en selectedTimeRef
    // en el tick anterior: re-leer el puntero para el reloj maestro (evita dejarlo ~5 min al Stop).
    const atPlay = clampToWindow(selectedTimeRef.current, ws, we);
    masterPlaybackStartRef.current = atPlay;
    masterWallStartRef.current = Date.now();
    setPlaybackStartTimeForDisplay(atPlay);
    playbackSpeedRef.current = playbackSpeedLatestRef.current;

    /**
     * Siempre 0 al pulsar Play: “segundos transcurridos desde Play” en la UI. No mezclar con el offset
     * del buffer (puntero − inicio de ventana HLS con pre-roll), o el reloj suma el pre-roll y desfasa la UI.
     */
    setMasterElapsedSeconds(0);

    // 1. NO se re-posiciona ninguna cámara al pulsar Play. Cada <video> ya quedó en su ancla al
    //    cargar el m3u8 (o tras Stop / navegación de timeline). Re-seekar aquí vaciaba el buffer
    //    y hacía saltar el .ts. Cada cámara reanuda con su propio buffer; el reloj maestro se
    //    deriva del vídeo real (ver tick en startPlaybackLoops), así que la hora mostrada sigue
    //    siendo coherente sin forzar que todas las cámaras compartan el mismo instante.

    // 2. Cambiar modo → isPlaying=true se propaga a todos los CameraVideoPlayer via prop
    setMode('PLAYING');
    modeRef.current = 'PLAYING';

    // 3. Promise.all: llamar play() en todos los reproductores registrados simultáneamente.
    //    Minimiza el desfase de arranque entre cámaras.
    await Promise.all(
      Array.from(playerRefsRef.current.entries()).map(([sid, ref]) => {
        const slotData = slotsRef.current.find((s) => s.id === sid)?.playback;
        if (!slotData || !ref.current) return Promise.resolve();
        return ref.current.play().catch(() => {/* autoplay blocked → el efecto isPlaying se encarga */});
      })
    );

    readySlots.forEach((s) => {
      if (s.camera && s.playback) assignRecordingViewMeta(s.id, s.camera, s.playback);
    });

    startPlaybackLoops();
  }, [prepareStream, prepareBatchStreams, isVisibleSlot, setSelectedTime, scheduleMasterReanchorAfterRecordingRoll, startPlaybackLoops, assignRecordingViewMeta]);

  /**
   * Al cambiar playbackSpeed durante PLAYING: el maestro usa (wallΔ)×velocidad. Si la velocidad
   * cambia sin re-anclar, expectedSec salta y el sync hace seeks en bucle → HLS recarga sin parar.
   * Re-anclamos "instante de grabación actual" y wallStart; los intervalos leen siempre playbackSpeedRef.
   */
  useEffect(() => {
    const prev = playbackSpeedPrevForAnchorRef.current;
    if (Math.abs(prev - playbackSpeed) < 0.0001) return;

    if (modeRef.current === 'PLAYING') {
      const wallNow = Date.now();
      const wallDeltaSec = (wallNow - masterWallStartRef.current) / 1000;
      if (Number.isFinite(wallDeltaSec) && wallDeltaSec >= 0) {
        const advanceRecordingSec = wallDeltaSec * prev;
        const nextStart = new Date(masterPlaybackStartRef.current.getTime() + advanceRecordingSec * 1000);
        masterPlaybackStartRef.current = nextStart;
        masterWallStartRef.current = wallNow;
        setPlaybackStartTimeForDisplay(nextStart);
        setMasterElapsedSeconds(0);
        syncWarmupUntilRef.current = wallNow + 10_000;
        syncSeekCooldownUntilRef.current.clear();
        postLocalSeekSyncGuardUntilRef.current = Math.max(
          postLocalSeekSyncGuardUntilRef.current,
          wallNow + 9_000
        );
      }
    }

    playbackSpeedPrevForAnchorRef.current = playbackSpeed;
    playbackSpeedRef.current = playbackSpeed;
  }, [playbackSpeed]);

  /**
   * Navegar en el timeline (drag/clic).
   *
   * Flujo de ciclo de vida correcto cuando cambia el timestamp:
   *   - El backend genera un M3U8 DIFERENTE por cada startTime.
   *   - Por tanto NO se puede hacer seekTo() sobre la instancia HLS existente.
   *   - La estrategia correcta es re-preparar (destruir + recrear la instancia HLS)
   *     con la nueva URL que incluye el nuevo timestamp.
   *   - La destrucción/recreación la hace CameraDropZone al detectar que `playbackKey`
   *     (compuesto por playlistUrl) cambió → React desmonta y monta CameraVideoPlayer.
   *
   * Debounce (300 ms): el drag genera decenas de eventos por segundo.
   *   Sólo ejecutamos el costoso prepareStream cuando el usuario se detiene.
   *
   * startTimeOverride: se pasa explícitamente para evitar que prepareStream lea
   *   el `selectedTime` viejo de su closure (la actualización de state es asíncrona).
   *
   * PLAYING → re-preparar + actualizar master clock cuando READY.
   * STOPPED → re-preparar para que el primer frame muestre preview del nuevo tiempo;
   *   NO llamar play() (CameraVideoPlayer respeta isPlayingProp=false después del Fix 1).
   */
  const setTimelinePosition = useCallback(
    (percent: number) => {
      const p = Math.max(0, Math.min(100, percent));
      const ms = timelineWindowStartRef.current.getTime() + (p / 100) * TIMELINE_MS;
      const raw = new Date(ms);
      const clamped = clampToWindow(raw, timelineWindowStartRef.current, timelineWindowEndRef.current);
      // Evita setState+debounce re-peticiones cuando el instante no cambia (mismo % / micro-jitter
      // reflotando) → sin esto, new Date(ms) re-renderiza aunque ms sea igual e invalida cadenas re-preparar.
      if (Math.abs(clamped.getTime() - selectedTimeRef.current.getTime()) < 1) {
        return;
      }
      setSelectedTimeState(clamped);
      selectedTimeRef.current = clamped;

      // Debounce: cancel any pending re-preparation from a previous drag event.
      if (timelineDebounceRef.current) clearTimeout(timelineDebounceRef.current);

      timelineDebounceRef.current = setTimeout(() => {
        const activeSlots = slotsRef.current.filter(
          (s) => isVisibleSlot(s.id) && s.camera && (s.streamStatus === 'READY' || s.streamStatus === 'PREPARING')
        );

        if (activeSlots.length === 0) return;

        // Split slots: those whose HLS window already covers newTime (local seek only)
        // vs those that need a new m3u8 (target outside loaded window or no window yet).
        const slotsInRange: typeof activeSlots = [];
        const slotsOutOfRange: typeof activeSlots = [];

        for (const s of activeSlots) {
          if (!s.playback || s.streamStatus === 'PREPARING') {
            slotsOutOfRange.push(s);
            continue;
          }
          const target = clamped.getTime();
          if (target >= s.playback.startTime.getTime() && target <= s.playback.endTime.getTime()) {
            slotsInRange.push(s);
          } else {
            slotsOutOfRange.push(s);
          }
        }

        // In-range: seek the video element — no new m3u8 request.
        if (slotsInRange.length > 0) {
          if (modeRef.current === 'PLAYING') {
            masterPlaybackStartRef.current = clamped;
            masterWallStartRef.current = Date.now();
            setMasterElapsedSeconds(0);
            syncWarmupUntilRef.current = Date.now() + 5000;
            syncSeekCooldownUntilRef.current.clear();
            setPlaybackStartTimeForDisplay(clamped);
          }
          slotsInRange.forEach((s) => {
            const pb = s.playback!;
            const anchor = pb.startOffsetSeconds ?? 0;
            const seekSec = anchor + Math.max(0, (clamped.getTime() - pb.startTime.getTime()) / 1000);
            playerRefsRef.current.get(s.id)?.current?.seekTo(seekSec);
          });
        }

        // Out-of-range: re-prepare with a new m3u8.
        slotsOutOfRange.forEach((s) => {
          prepareStream(s.id, undefined, clamped).then((ok) => {
            if (!ok) return;
            if (modeRef.current === 'PLAYING') {
              masterPlaybackStartRef.current = clamped;
              masterWallStartRef.current = Date.now();
              setMasterElapsedSeconds(0);
              syncWarmupUntilRef.current = Date.now() + 5000;
              syncSeekCooldownUntilRef.current.clear();
              setPlaybackStartTimeForDisplay(clamped);
            }
          });
        });
      }, 300);
    },
    [prepareStream, isVisibleSlot]
    // Note: `mode` is intentionally omitted — we read modeRef.current in the callback
    // to avoid recreating the function on every play/stop toggle.
  );

  /**
   * Salto rápido de ±N segundos.
   *  - Si el objetivo cae dentro de la intersección [start,end] de los slots READY, se hace
   *    seek local sobre el HLS ya cargado (sin nueva petición).
   *  - Si cae FUERA de la ventana cacheada (p. ej. ±5 min y el cache es 5 atrás + 15 adelante),
   *    se re-prepara con un nuevo ancla centrado en el objetivo. Esto cumple la regla
   *    "sin recarga salvo que se exceda la ventana" sin dejar al usuario "atascado" en el límite.
   */
  const seekBySecondsLocal = useCallback(
    (deltaSec: number): SeekBySecondsLocalResult => {
      const tws = timelineWindowStartRef.current;
      const twe = timelineWindowEndRef.current;
      // Snapshot único — evita que `Date.now()` avance entre el cálculo del baseMs
      // (origen del salto) y el reanclaje del reloj maestro (Date.now() posterior).
      // Con velocidad 5×, 50 ms entre llamadas son 250 ms de drift visible.
      const seekStartedAtMs = Date.now();

      // Preferir la posición REAL de un slot READY como ancla del salto: el reloj maestro
      // adelanta al video por buffering, y usarlo deja al usuario con un retroceso "corto"
      // (p. ej. -13 s en vez de los -15 s pedidos).
      const referenceSlot = slotsRef.current.find(
        (s) => isVisibleSlot(s.id) && s.streamStatus === 'READY' && s.playback
      );
      const referenceRef = referenceSlot ? playerRefsRef.current.get(referenceSlot.id) : null;
      const referenceCurrentSec = referenceRef?.current?.getCurrentTime?.();

      let baseMs: number;
      if (
        referenceSlot &&
        typeof referenceCurrentSec === 'number' &&
        Number.isFinite(referenceCurrentSec) &&
        referenceCurrentSec > 0
      ) {
        const pb = referenceSlot.playback!;
        const offset = pb.startOffsetSeconds ?? 0;
        baseMs = pb.startTime.getTime() + Math.max(0, referenceCurrentSec - offset) * 1000;
      } else if (modeRef.current === 'PLAYING' && masterWallStartRef.current > 0) {
        const speed = playbackSpeedRef.current;
        const elapsed = ((seekStartedAtMs - masterWallStartRef.current) / 1000) * speed;
        baseMs = masterPlaybackStartRef.current.getTime() + elapsed * 1000;
      } else {
        baseMs = selectedTimeRef.current.getTime();
      }

      let rawTargetMs = baseMs + deltaSec * 1000;
      rawTargetMs = Math.max(tws.getTime(), Math.min(twe.getTime(), rawTargetMs));

      const activeSlots = slotsRef.current.filter(
        (s) => isVisibleSlot(s.id) && s.camera && s.streamStatus === 'READY' && s.playback
      );

      if (activeSlots.length === 0) {
        return { ok: false, reason: 'no_ready_slots' };
      }

      const loadedStart = Math.max(...activeSlots.map((s) => s.playback!.startTime.getTime()));
      const loadedEnd = Math.min(...activeSlots.map((s) => s.playback!.endTime.getTime()));
      if (loadedStart > loadedEnd) {
        return { ok: false, reason: 'no_window_overlap' };
      }

      // Fuera de la ventana cacheada: re-preparar HLS centrado en el nuevo objetivo.
      // Por defecto: pre-roll + forward (cap `RECORDING_MAX_PLAYLIST_MINUTES` vs backend).
      const outOfWindow = rawTargetMs < loadedStart || rawTargetMs > loadedEnd;
      if (outOfWindow) {
        const newTime = new Date(rawTargetMs);

        if (timelineDebounceRef.current) {
          clearTimeout(timelineDebounceRef.current);
          timelineDebounceRef.current = null;
        }

        setSelectedTime(newTime);

        const slotsToReprepare = slotsRef.current.filter(
          (s) =>
            isVisibleSlot(s.id) &&
            s.camera &&
            (s.streamStatus === 'READY' || s.streamStatus === 'PREPARING')
        );

        slotsToReprepare.forEach((s) => {
          prepareStream(s.id, undefined, newTime).then((ok) => {
            if (!ok) return;
            if (modeRef.current === 'PLAYING') {
              masterPlaybackStartRef.current = newTime;
              masterWallStartRef.current = Date.now();
              setMasterElapsedSeconds(0);
              syncWarmupUntilRef.current = Date.now() + 5000;
              syncSeekCooldownUntilRef.current.clear();
              setPlaybackStartTimeForDisplay(newTime);
            }
          });
        });

        return { ok: true };
      }

      const targetMs = rawTargetMs;
      if (Math.abs(targetMs - baseMs) < 250) {
        return { ok: true };
      }

      const newTime = new Date(targetMs);

      if (timelineDebounceRef.current) {
        clearTimeout(timelineDebounceRef.current);
        timelineDebounceRef.current = null;
      }

      setSelectedTime(newTime);

      if (modeRef.current === 'PLAYING') {
        const guardUntil = seekStartedAtMs + 10_000;
        masterPlaybackStartRef.current = newTime;
        // Usar el mismo snapshot que se utilizó para baseMs — así newTime y wallStart
        // referencian el mismo instante y el reloj maestro no arranca "adelantado".
        masterWallStartRef.current = seekStartedAtMs;
        setMasterElapsedSeconds(0);
        setPlaybackStartTimeForDisplay(newTime);
        syncWarmupUntilRef.current = guardUntil;
        syncSeekCooldownUntilRef.current.clear();
        postLocalSeekSyncGuardUntilRef.current = guardUntil;
      }

      activeSlots.forEach((s) => {
        const pb = s.playback!;
        const anchor = pb.startOffsetSeconds ?? 0;
        const seekSec = anchor + Math.max(0, (targetMs - pb.startTime.getTime()) / 1000);
        const ref = playerRefsRef.current.get(s.id);
        ref?.current?.seekTo(seekSec, { skipIfClose: true });
      });

      return { ok: true };
    },
    [isVisibleSlot, setSelectedTime, prepareStream]
  );

  const seekTilesToRecordingWindowStart = useCallback(
    (newTime: Date, opts: { updateMasterIfPlaying: boolean }) => {
      if (timelineDebounceRef.current) {
        clearTimeout(timelineDebounceRef.current);
        timelineDebounceRef.current = null;
      }

      const ready = slotsRef.current.filter(
        (s) => isVisibleSlot(s.id) && s.camera && s.streamStatus === 'READY' && s.playback
      );

      if (ready.length === 0) {
        const tws = timelineWindowStartRef.current;
        setSelectedTime(tws);
        return;
      }

      setSelectedTime(newTime);

      const now = Date.now();
      if (opts.updateMasterIfPlaying && modeRef.current === 'PLAYING') {
        const guardUntil = now + 10_000;
        masterPlaybackStartRef.current = newTime;
        masterWallStartRef.current = now;
        setMasterElapsedSeconds(0);
        setPlaybackStartTimeForDisplay(newTime);
        syncWarmupUntilRef.current = guardUntil;
        syncSeekCooldownUntilRef.current.clear();
        postLocalSeekSyncGuardUntilRef.current = guardUntil;
      } else {
        postLocalSeekSyncGuardUntilRef.current = Math.max(
          postLocalSeekSyncGuardUntilRef.current,
          now + 4500
        );
      }

      ready.forEach((s) => {
        const pb = s.playback!;
        const anchor = pb.startOffsetSeconds ?? 0;
        const seekSec =
          anchor + Math.max(0, (newTime.getTime() - pb.startTime.getTime()) / 1000);
        const ref = playerRefsRef.current.get(s.id);
        ref?.current?.seekTo(seekSec);
      });
    },
    [isVisibleSlot, setSelectedTime]
  );

  const resetPointerToLoadedRecordingStart = useCallback(() => {
    const ready = slotsRef.current.filter(
      (s) => isVisibleSlot(s.id) && s.camera && s.streamStatus === 'READY' && s.playback
    );
    if (ready.length === 0) {
      if (timelineDebounceRef.current) {
        clearTimeout(timelineDebounceRef.current);
        timelineDebounceRef.current = null;
      }
      const tws = timelineWindowStartRef.current;
      setSelectedTime(tws);
      return;
    }
    const startMs = Math.min(...ready.map((s) => s.playback!.startTime.getTime()));
    const newTime = clampToWindow(
      new Date(startMs),
      timelineWindowStartRef.current,
      timelineWindowEndRef.current
    );
    seekTilesToRecordingWindowStart(newTime, { updateMasterIfPlaying: true });
  }, [isVisibleSlot, seekTilesToRecordingWindowStart, setSelectedTime]);

  const stopAndResetToRecordingStart = useCallback(() => {
    Array.from(recordingViewMetaRef.current.keys()).forEach((id) => {
      flushRecordingViewAuditForSlot(id);
    });
    setMode('STOPPED');
    modeRef.current = 'STOPPED';
    setPlaybackStartTimeForDisplay(null);
    setMasterElapsedSeconds(0);
    if (syncIntervalRef.current) {
      clearInterval(syncIntervalRef.current);
      syncIntervalRef.current = null;
    }
    if (tickIntervalRef.current) {
      clearInterval(tickIntervalRef.current);
      tickIntervalRef.current = null;
    }
    syncSeekCooldownUntilRef.current.clear();
    postLocalSeekSyncGuardUntilRef.current = 0;
    bumpHlsStabilityGuard();
    if (timelineDebounceRef.current) {
      clearTimeout(timelineDebounceRef.current);
      timelineDebounceRef.current = null;
    }

    const ready = slotsRef.current.filter(
      (s) => isVisibleSlot(s.id) && s.camera && s.streamStatus === 'READY' && s.playback
    );
    const tws = timelineWindowStartRef.current;
    const twe = timelineWindowEndRef.current;
    if (ready.length === 0) {
      setSelectedTime(tws);
      return;
    }
    // Stop → puntero al instante elegido por el usuario (timelineAnchorWallTime), no al inicio
    // del ventana HLS: el playlist arranca ~5 min antes del ancla por el preroll, y rebobinar
    // ahí dejaba el vídeo en, p.ej. 9:52 cuando el usuario había seleccionado 9:57.
    const targetMs = Math.min(
      ...ready.map((s) => {
        const pb = s.playback!;
        const anchor = pb.timelineAnchorWallTime;
        return anchor != null && Number.isFinite(anchor.getTime())
          ? anchor.getTime()
          : pb.startTime.getTime();
      })
    );
    const targetWall = clampToWindow(new Date(targetMs), tws, twe);
    seekTilesToRecordingWindowStart(targetWall, { updateMasterIfPlaying: false });
  }, [bumpHlsStabilityGuard, flushRecordingViewAuditForSlot, isVisibleSlot, seekTilesToRecordingWindowStart, setSelectedTime]);

  useEffect(() => {
    return () => {
      if (syncIntervalRef.current) clearInterval(syncIntervalRef.current);
      if (tickIntervalRef.current) clearInterval(tickIntervalRef.current);
      if (timelineDebounceRef.current) clearTimeout(timelineDebounceRef.current);
      Array.from(recordingViewMetaRef.current.keys()).forEach((id) => {
        flushRecordingViewAuditForSlot(id);
      });
    };
  }, [flushRecordingViewAuditForSlot]);

  useEffect(() => {
    // Keep slot state when shrinking layouts (2x2 -> 1x2 -> 1x1).
    // We only grow the backing array when needed; we never shrink it,
    // so hidden slots preserve their assigned cameras and stream state.
    if (slots.length < n) {
      setSlots((prev) => {
        const next = [...prev];
        for (let i = prev.length; i < n; i++) {
          next.push({ id: i + 1, camera: null, streamStatus: 'IDLE' as StreamStatus });
        }
        return next;
      });
    }
  }, [n, slots.length]);

  const prevVisibleNRef = useRef(n);
  useLayoutEffect(() => {
    const prevN = prevVisibleNRef.current;
    prevVisibleNRef.current = n;
    if (n >= prevN) return;

    setSlots((prev) => {
      const camerasInHidden = prev.slice(n).filter((s) => s.camera != null);
      if (camerasInHidden.length === 0) return prev;

      const emptyInVisible = prev.slice(0, n).filter((s) => s.camera == null);
      if (emptyInVisible.length === 0) return prev;

      const next = [...prev];
      let moved = 0;
      for (const source of camerasInHidden) {
        if (moved >= emptyInVisible.length) break;
        const target = emptyInVisible[moved]!;
        const tIdx = next.findIndex((s) => s.id === target.id);
        const sIdx = next.findIndex((s) => s.id === source.id);
        next[tIdx] = {
          ...next[tIdx],
          camera: source.camera,
          streamStatus: source.streamStatus,
          playback: source.playback,
          error: source.error,
          origin: source.origin,
        };
        next[sIdx] = { id: next[sIdx].id, camera: null, streamStatus: 'IDLE' as StreamStatus };
        moved++;
      }
      return next;
    });

    slotsRef.current.slice(n).forEach((s) => {
      if (s.camera) bumpPrepareGeneration(s.id);
    });
  }, [n, bumpPrepareGeneration]);

  return {
    mode,
    selectedTime,
    slots,
    timelinePosition,
    timelineWindowStart,
    playbackStartTimeForDisplay,
    masterElapsedSeconds,
    playbackSpeed,
    globalError,
    setGlobalError,
    addCameraToSlot,
    removeCameraFromSlot,
    play,
    pause,
    stop,
    setTimelinePosition,
    seekBySecondsLocal,
    setSelectedTime,
    setPlaybackSpeed,
    registerPlayer,
    unregisterPlayer,
    getSlotPlaybackTime,
    prepareStream,
    resetPointerToLoadedRecordingStart,
    stopAndResetToRecordingStart,
    bumpHlsStabilityGuard,
    forceRecordingSlotsPrepareAtTime,
    extendRecordingHlsWindowWhilePlaying,
  };
}
